import { useState } from 'react';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  useRestoreFocusTarget,
} from '@fluentui/react-components';
import {
  ArrowRight20Regular,
  ChevronRight20Regular,
  Dismiss20Regular,
  Info20Filled,
} from '@fluentui/react-icons';
import { Link } from 'react-router-dom';
import { useShellAuth } from '../../auth/ShellAuthContext';
import './HomePage.css';

const almRoutes = {
  submitRequest: '/alm/submitRequest',
  apps: '/alm/apps',
};

const publishingSteps = [
  {
    step: '01 / PREPARE',
    title: 'Start with your idea',
    description: 'Gather the purpose, ownership, and compliance details for the app or agent you want to publish.',
  },
  {
    step: '02 / SUBMIT',
    title: 'Send it for review',
    description: 'Submit your LOB publishing request and follow its progress through the review process.',
  },
  {
    step: '03 / MANAGE',
    title: 'Stay in control',
    description: 'Explore your apps and agents, and keep track of the requests that need your attention.',
  },
];

const assetCategories = [
  { label: 'BUILD & AUTOMATE', title: 'Power Platform', description: 'Environments, apps, flows, and connectors.' },
  { label: 'SHARE & COLLABORATE', title: 'Connected workspaces', description: 'SharePoint sites, Loop workspaces, and Microsoft 365 groups.' },
  { label: 'ANALYZE & REPORT', title: 'Power BI', description: 'Workspaces and their lifecycle.' },
];

export function HomePage() {
  const { user } = useShellAuth();
  const displayName = user.name?.trim() || user.username;
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const whatsNewTriggerAttributes = useRestoreFocusTarget();

  return (
    <div className="home-page">
      <section className="home-hero" aria-labelledby="home-title">
        <div className="home-hero-content">
          <h1 id="home-title">
            Build what&apos;s next.<br />
            Simplify compliance across your digital assets.
          </h1>
          <p>
            See all your Apps, Agents, and Digital Assets from the tenant in one place, build apps
            and agents for your team, and keep it all compliant&mdash;through one connected experience.
          </p>
        </div>
      </section>

      <div className="home-content">
        <section className="home-announcement" aria-label="Announcements">
          <Info20Filled aria-hidden="true" />
          <div>
            <strong>App &amp; Agent Lifecycle Management is now in GovernanceHub</strong>
            <p>Access and submit your LOB publishing requests, view inventory, and drive digital asset governance from one unified portal.</p>
          </div>
          <Button
            {...whatsNewTriggerAttributes}
            appearance="transparent"
            className="home-announcement-action"
            aria-haspopup="dialog"
            aria-expanded={whatsNewOpen}
            onClick={() => setWhatsNewOpen(true)}
          >
            See what&apos;s new <ArrowRight20Regular aria-hidden="true" />
          </Button>
          <Drawer
            className="home-update-panel"
            aria-label="What's new in GovernanceHub"
            open={whatsNewOpen}
            position="end"
            type="overlay"
            onOpenChange={(_, data) => setWhatsNewOpen(data.open)}
          >
            <DrawerHeader className="home-update-header">
              <DrawerHeaderTitle
                className="home-update-header-title"
                action={
                  <Button
                    appearance="subtle"
                    aria-label="Close what's new"
                    icon={<Dismiss20Regular />}
                    onClick={() => setWhatsNewOpen(false)}
                  />
                }
              >
                What&apos;s new in GovernanceHub
              </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody className="home-update-body">
              <p><time dateTime="2026-10">October 2026</time></p>
              <h3>App &amp; Agent Lifecycle Management is now in GovernanceHub</h3>
              <p>You can now access Apps &amp; Agents and Digital Assets from one portal.</p>
              <ul>
                <li>Submit LOB apps and agents for publishing and follow your requests through review.</li>
                <li>Find your apps and agents, and manage them throughout their lifecycle.</li>
                <li>Continue governing your digital assets using the dedicated Digital Assets navigation.</li>
              </ul>
              <p>We&apos;re rolling out in stages. Continue using the existing portals for journeys that haven&apos;t migrated, and follow rollout communications for availability and next steps.</p>
              <p>
                <a href="https://microsoft.sharepoint.com/sites/microsoft365builders/sitepages/publish/alm-submission.aspx" target="_blank" rel="noopener noreferrer">
                  Read the App Lifecycle Management submission documentation <span aria-hidden="true">&#8599;</span>
                </a>
              </p>
            </DrawerBody>
          </Drawer>
        </section>

        <section className="home-welcome" aria-labelledby="home-welcome-title">
          <h2 id="home-welcome-title">Welcome back{displayName ? `, ${displayName}` : ''}</h2>
          <p>Manage your requests, approvals, apps, agents, and digital assets from one place.</p>
        </section>

        <div className="home-layout">
          <div className="home-workspaces">
            <section className="home-workspace" aria-labelledby="home-apps-title">
              <div className="home-section-header">
                <div>
                  <h2 id="home-apps-title">Apps &amp; Agents</h2>
                  <p>Submit new LOB Apps and Agents for publishing and manage their lifecycle and compliance till they are retired.</p>
                </div>
                <Link to={almRoutes.apps}>
                  View all apps &amp; agents <ArrowRight20Regular aria-hidden="true" />
                </Link>
              </div>
              <div className="home-section-body">
                <p>From initial submission through ongoing lifecycle management, GovernanceHub brings your publishing journey together.</p>
                <ol className="home-publishing-steps">
                  {publishingSteps.map(({ step, title, description }) => (
                    <li key={step}>
                      <span className="home-eyebrow">{step}</span>
                      <h3>{title}</h3>
                      <p>{description}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </section>

            <section className="home-workspace" aria-labelledby="home-assets-title">
              <div className="home-section-header">
                <div>
                  <h2 id="home-assets-title">Digital Assets</h2>
                  <p>Understand ownership, policy compliance, lifecycle health, and governance risks across Microsoft 365 assets.</p>
                </div>
              </div>
              <div className="home-section-body">
                <p>Find the assets you own, understand their policy context, and keep them governed throughout their lifecycle.</p>
                <div className="home-digital-estate">
                  <div className="home-estate-intro">
                    <span className="home-eyebrow">YOUR DIGITAL ESTATE</span>
                    <h3>Know what you own.<br />Keep it governed.</h3>
                    <p>Find the right asset, understand its ownership and policy context, and take the next step when something needs review.</p>
                  </div>
                  <div className="home-asset-categories">
                    {assetCategories.map(({ label, title, description }) => (
                      <div key={title}>
                        <span className="home-eyebrow">{label}</span>
                        <h3>{title}</h3>
                        <p>{description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <aside className="home-right-rail" aria-label="Home quick actions">
            <section aria-labelledby="home-actions-title">
              <div className="home-rail-header">
                <h2 id="home-actions-title">Quick actions</h2>
                <span>Common tasks</span>
              </div>
              <Link className="home-quick-action" to={almRoutes.submitRequest}>
                <span className="home-quick-action-icon" aria-hidden="true">P</span>
                <span><strong>Submit a LOB agent for publishing</strong><span>Start an agent publishing request</span></span>
                <ChevronRight20Regular aria-hidden="true" />
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
